<template>
    <td
        role="cell"
        :style="containerStyle"
        :class="containerClass"
        @click="setFocus"
        :data-p-frozen-column="frozen"
        :data-p-editable-column="editableCell"
        ref="cellRef"
    >
        <div class="md-editable-grid-cell-wrapper" @dblclick="onCellDblClick">
            <!-- Editing mode -->
            <div v-if="isEditing" class="editable-lookup-cell-content-wrapper">
                <div
                    :class="{
                        'md-editable-grid-cell-content-invalid': invalid,
                        'md-editable-grid-cell-editing': !invalid
                    }"
                    class="md-editable-grid-cell-content"
                    contenteditable="true"
                    ref="inputRef"
                    @keydown="handleKeydown"
                    @blur="finishEdit"
                >
                    {{ valueFormatted }}
                </div>

                <div
                    class="editable-lookup-cell-icon-container"
                    @mousedown.prevent="callModalTable"
                >
                    <i class="ikon med-zoom fw-bold"></i>
                </div>
            </div>

            <!-- Display mode -->
            <div v-else class="editable-lookup-cell-display-wrapper">
                <span
                    class="md-editable-grid-cell-display"
                    :class="{ 'md-editable-grid-cell-non-editable': !editable }"
                    @dblclick="requestEdit"
                    @keydown="handleDisplayKeydown"
                    tabindex="0"
                    ref="displayRef"
                >
                    {{ valueFormatted }}
                </span>
                <div
                    v-if="editableCell"
                    class="editable-lookup-cell-display-icon"
                    @mousedown.prevent="requestEdit"
                >
                    <i class="ikon med-zoom fw-bold"></i>
                </div>
            </div>
        </div>

        <ModalTablePaginationServer
            :Resource="this.resourceParameter"
            :TypeOption="this.refCode"
            @getCodeConditionOK="codeConditionOkHandler($event)"
            @getCodeConditionCancel="codeConditionCancelHandler($event)"
            :Fields="this.fields"
            :FieldCode="this.fieldCode"
            :FieldDescription="this.fieldDescription"
            :TitleWindow="this.searchTitle"
            :ModalId="this.modalId"
            :AuxFields="this.auxFields"
            :FieldsDate="this.fieldsDate"
            :ShowAddRecord="this.showAddRecord"
            :AddRecordLabel="this.addRecordLabel"
            :valueToSearch="this.valueToSearch"
        >
            <template v-if="$slots.body" v-slot:body>
                <slot name="body" />
            </template>
        </ModalTablePaginationServer>
    </td>
</template>

<script>
import EditableCellBase from './EditableCellBase.vue';
import ShowMessage from '@/components/messages/ShowMessage.js';
import Utils from '@/common/utils.js';
import ModalService from '@/services/modal.service';

export default {
    name: 'EditableLookupCell',
    mixins: [ShowMessage],
    inject: ['$parentInstance'],
    extends: EditableCellBase,
    props: {
        value: [String, Number],
        api: { type: String, required: true },
        fieldCode: { type: String, default: 'code' },
        fieldDescription: { type: String, default: 'description' },
        valueDescription: { type: [String, Number, null], default: null },
        searchTitle: { type: String, required: true },
        searchFieldCode: { type: String, default: 'Code' },
        fields: Array,
        auxFields: Array,
        fieldsDate: Array,
        showAddRecord: { type: Boolean, default: false },
        addRecordLabel: String
    },
    data() {
        const defaultLookupInfo = {
            fieldName: null,
            code: null,
            description: null,
            auxFields: []
        };

        return {
            modalId: Utils.generateUUID(),
            fieldsParameter: [],
            resourceParameter: '',
            refCode: Utils.generateUUID(),
            lookupActive: false,
            valueBackup: {
                value: this.value,
                description: this.valueDescription
            },
            dataLookup: { ...defaultLookupInfo },
            defaultLookupInfo: defaultLookupInfo,
            valueToSearch: null
        };
    },
    computed: {
        valueFormatted() {
            if (this.formatter) {
                return this.formatter(this.value);
            } else if (this.valueDescription && Object.hasOwn(this.item, this.valueDescription)) {
                return this.item[this.valueDescription];
            }

            return this.value;
        }
    },
    watch: {
        value(newValue) {
            this.valueBackup.value = newValue;
            this.valueToSearch = newValue;
        }
    },
    methods: {
        startEdit(char) {
            if (!this.editable) {
                this.displayContentFocus();
                return;
            }

            this.isEditing = true;
            this.dataLookup = { ...this.defaultLookupInfo };
            if (char !== undefined) {
                this.$nextTick(() => {
                    const el = this.$refs.inputRef;
                    if (el) {
                        el.innerText = char;
                        el.focus();
                        this.moveCaretToEnd(el);
                    }
                });
            } else {
                this.inputContentFocus();
            }
        },

        async finishEdit(event) {
            if (this.lookupActive === false && this.isEditing) {
                this.isEditing = false;
                const val = event.target.innerText.trim();

                if (val === this.dataLookup.description) {
                    let event = this.createEventData(this.dataLookup.code);
                    event.lookupInfo = this.dataLookup;

                    if (this.isAppendSection) {
                        this.$emit('input', this.dataLookup);
                    } else {
                        this.$parentInstance.cellEditComplete(event);
                    }

                    this.validateCell(this.dataLookup.code);

                    if (this.invalid) {
                        this.showMessage(
                            this.$tc('Msg.Error'),
                            this.invalidMessage(this.name),
                            'errorAutoHide'
                        );
                    }
                } else {
                    const changed = val !== String(this.valueFormatted).trim();

                    if (changed) {
                        const resolved = await this.getLookup(val);

                        if (resolved) {
                            if (this.isAppendSection) {
                                this.$emit('input', this.dataLookup);
                            } else {
                                let evt = this.createEventData(this.dataLookup.code);
                                evt.lookupInfo = this.dataLookup;
                                this.$parentInstance.cellEditComplete(evt);
                            }
                        } else {
                            // No match — clear the value and mark invalid
                            const emptyLookup = { ...this.defaultLookupInfo };
                            if (this.isAppendSection) {
                                this.$emit('input', emptyLookup);
                            } else {
                                let evt = this.createEventData(null);
                                evt.lookupInfo = emptyLookup;
                                this.$parentInstance.cellEditComplete(evt);
                            }
                            this.invalid = true;
                        }
                    }
                }
            }

            this.dataLookup = { ...this.defaultLookupInfo };
        },

        openModal() {
            if (!this.editableCell) {
                this.onCellDblClick();
                return;
            }

            if (!this.isEditing) {
                this.isEditing = true;
                this.dataLookup = { ...this.defaultLookupInfo };
                this.$nextTick(() => {
                    const el = this.$refs.inputRef;
                    if (el) {
                        el.innerText = String(this.valueFormatted || '');
                    }
                    this.callModalTable();
                });
            } else {
                this.callModalTable();
            }
        },

        callModalTable() {
            const el = this.$refs.inputRef;
            this.valueToSearch = el.innerText;
            this.lookupActive = true;
            this.fieldsParameter = [this.fieldCode, this.fieldDescription];
            this.resourceParameter = '/' + this.api;

            this.$bvModal.show(this.modalId);
        },

        codeConditionOkHandler(result) {
            this.lookupActive = false;
            var type = result.FieldName;

            if (type.indexOf(this.refCode) !== -1 && result.code) {
                this.dataLookup.fieldName = this.refCode;
                this.dataLookup.code = result.code.trim();
                this.dataLookup.description = result.description.trim();
                this.dataLookup.auxFields = result.AuxFields;

                const el = this.$refs.inputRef;
                el.innerText = result.description;
                this.validateCell(result.code);
            }

            this.resourceParameter = '';
            this.$bvModal.hide(this.modalId);
            this.inputContentFocus();
        },

        codeConditionCancelHandler() {
            this.lookupActive = false;
            this.resourceParameter = '';
            this.inputContentFocus();
        },

        async getLookup(value) {
            var result = true;

            if (Utils.isNullOrEmptyString(value) === false) {
                let parameter = {};
                if (this.searchFieldCode) {
                    parameter[this.searchFieldCode] = value;
                } else {
                    parameter.SearchCriteria = value;
                }

                await ModalService.getAll('/' + this.api, parameter)
                    .then(response => {
                        if (response.status == '200' && response.data && response.data.length > 0) {
                            this.dataLookup.fieldName = this.refCode;
                            this.dataLookup.code = response.data[0][this.fieldCode];
                            this.dataLookup.description = response.data[0][this.fieldDescription];

                            if (this.auxFields) {
                                this.dataLookup.auxFields = [];
                                this.auxFields.forEach(element => {
                                    this.dataLookup.auxFields.push(response.data[0][element]);
                                });
                            }

                            this.validateCell(this.dataLookup.code);
                            if (this.invalid) {
                                this.showMessage(
                                    this.$tc('Msg.Error'),
                                    this.invalidMessage(this.name),
                                    'errorAutoHide'
                                );
                            }
                        } else {
                            result = false;
                        }
                    })
                    .catch(error => {
                        this.showResponseErrorMessage(error);
                        result = false;
                    });
            } else {
                this.validateCell('');
                this.dataLookup.fieldName = this.refCode;
                this.dataLookup.code = '';
                this.dataLookup.description = '';
                this.dataLookup.auxFields = [];

                if (this.invalid) {
                    this.showMessage(
                        this.$tc('Msg.Error'),
                        this.invalidMessage(this.name),
                        'errorAutoHide'
                    );
                }
            }

            return result;
        },

        requestEdit() {
            EditableCellBase.methods.requestEdit.call(this);

            this.$nextTick(() => {
                this.openModal();
            });
        }
    }
};
</script>

<style scoped>
.editable-lookup-cell-content-wrapper {
    display: flex;
    align-items: stretch;
    width: 100%;
    height: 100%;
    position: relative;
}

.editable-lookup-cell-icon-container {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #19bee8;
    cursor: pointer;
    z-index: 11;
}
tbody.md-editable-grid-body-append .editable-lookup-cell-icon-container {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #19bee8;
    cursor: pointer;
    z-index: 11;
}

/* Display mode: flex-based so the icon stays within the cell scroll area */
.editable-lookup-cell-display-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    overflow: hidden;
}
.editable-lookup-cell-display-wrapper .md-editable-grid-cell-display {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.editable-lookup-cell-display-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding-right: 6px;
    color: #19bee8;
    cursor: pointer;
}
tbody.md-editable-grid-body-append .editable-lookup-cell-display-icon {
    color: #19bee8;
}
</style>
